import { race, takeEvery, all, call, put, select, delay, spawn, take } from 'redux-saga/effects'
import { jwtDecode } from 'jwt-decode'

import * as actions from './actions'
import { authClient } from './helper'
import { util } from '../../util'
import { history } from '../../util/history'
import { constants } from '../../constants'

/**
 * This Generator starts the OAuth Authentication process
 */
function* signIn() {
    try {
        yield authClient.authorize()
    } catch (e) {
        console.log('e')
    }
}

/**
 * parseRedirectAuthInfo generator was invoked from redux store setup file (src/config/redux)
 * generators runs on every page loads ( User Force reloads or OAuth redirect load )
 */
function* parseRedirectAuthInfo() {
    const codeMatch = window.location.href.match('[?#&]code=([^&]*)')
    /**
     * If App is redirected from oAuth server then OAuth will append the code value in the URL eg: http://localhost:3000?code='****'
     *  OAuth -> App -> redux store (invokes the parseRedirectAuthInfo respective action dispatcher) -> parseRedirectAuthInfo*
     *                            ------------- OR -------------------
     * App -> redux store (invokes the parseRedirectAuthInfo respective action dispatcher) -> parseRedirectAuthInfo*
     */
    if (codeMatch && codeMatch[1]) {
        /**
         *  this condition executes only if user is redirected form OAuth with code.
         *  using the code value Make an POST request call for OAuth token endpoint..
         */
        try {
            yield put(actions.SettingUserInfo({ setting_user_info: true }))
            const {
                data: { access_token, id_token, refresh_token, expires_in },
            } = yield call(authClient.getTokens(codeMatch[1]))

            const { exp } = jwtDecode(id_token)

            process.env.REACT_APP_ENV !== 'production' &&
                console.log('Session Expires at', new Date(exp * 1000))

            /**
             *  Update Redux Auth with tokens and expires at values.
             */
            yield put(
                actions.setAuthSession({
                    accessToken: access_token,
                    idToken: id_token,
                    refreshToken: refresh_token,
                    expiresIn: expires_in,
                    refreshCount: 0,
                    is_logout: false,
                    expires_at: exp,
                })
            )
            /**
             * After Fetching tokens, yield for user info and start the session
             */
            yield* getCognitoUser(access_token)
            yield spawn(getUserSession)
            history.push('/dashboard')
            yield put(actions.SettingUserInfo({ setting_user_info: false }))
        } catch (error) {
            console.error('AUTH ERROR', error)
            yield signOut()
            document.write(error.response.data)
        }
    }

    if (!codeMatch) {
        /**
         * This condition executes on every user force page loads
         * If the user isn't authenticated then logout the user otherwise check for valid expiration time
         * If the current time passes the expiration time then logout the user otherwise continue the session.
         */
        const currentAuthInfo = yield select(util.getCurrentAuthInfo)
        if (currentAuthInfo.isAuth) {
            if (util.sessionExpiresInMS(currentAuthInfo.expires_at) > 0) {
                yield spawn(getUserSession)
            } else {
                yield signOut()
            }
        }
    }
}

function* getUserSession() {
    const currentAuthInfo = yield select(util.getCurrentAuthInfo)
    const currentTime = new Date().getTime()
    const timeDifference = currentAuthInfo.expires_at * 1000 - currentTime
    /**
     * If timeOut wins the race meaning session is completed, and we have to refresh the session
     * by providing the refresh token to token endpoint with refresh_token grant type.
     */
    const winner = yield race({
        timeOut: delay(timeDifference - 60000), // trigger refresh-token mechanism 1minute before expiration
        logout: take(actions.actionTypes.HANDLE_USER_LOGOUT),
    })

    if (winner.timeOut) {
        const { refreshCount } = yield select(util.getCurrentAuthInfo)
        if (refreshCount < process.env.REACT_APP_SSO_REFRESH_LIMIT) {
            try {
                const {
                    data: { access_token, id_token, expires_in, refresh_token },
                } = yield call(authClient.getRefreshToken(currentAuthInfo.refreshToken))

                const { exp } = jwtDecode(id_token)
                yield put(
                    actions.setAuthSession({
                        accessToken: access_token,
                        idToken: id_token,
                        expiresIn: expires_in,
                        refreshCount: refreshCount + 1,
                        expires_at: exp,
                        refreshToken: refresh_token,
                    })
                )
                yield* getCognitoUser(access_token)
                yield spawn(getUserSession)
            } catch (err) {
                console.log(err)
                yield signOut()
            }
        } else {
            console.log('SSO refresh token limit reached')
            yield put(actions.logout())
        }
    }
}

function* getCognitoUser(token) {
    try {
        const user = yield authClient.getUser(token)
        const filtered_permissions = user.data.permissions.filter(item => {
            return item.startsWith('APP-7NOWMANAGER-')
        })

        const { permissions } = constants
        const user_permissions = filtered_permissions
            .map(filtered_permission => permissions[filtered_permission])
            .filter(permission => permission)

        if (
            !filtered_permissions.filter(filtered_permission =>
                filtered_permission.endsWith('-READ')
            ).length
        ) {
            console.log('User without any 7Now Admin Portal Read Permission')
            yield signOut()

            return
        }

        yield put(
            actions.setUserInfo({
                user: user.data,
                isAuth: true,
                user_permissions,
            })
        )
    } catch (e) {
        console.log(e)
        yield signOut()
    }
}

function* signOut() {
    try {
        const { idToken } = yield select(util.getCurrentAuthInfo)
        if (idToken) {
            yield put(actions.clearAuth())
            yield authClient.logout(idToken)
        }
    } catch (e) {
        console.log(e)
    }
}

export default function* watchAuth() {
    yield all([takeEvery(actions.actionTypes.SAGA_SIGN_IN, signIn)])
    yield all([takeEvery(actions.actionTypes.REDIRECT_AUTH_INFO, parseRedirectAuthInfo)])
    yield all([takeEvery(actions.actionTypes.HANDLE_USER_LOGOUT, signOut)])
}
